/**
 * The provider's visit note and the close-out of a building's visit day (facility records Phase 0c, Isaiah Cotto
 * 2026-10-27; docs/facility-delivery/DESIGN.md). Mirrors backend/src/routes/emergeDaySheetVisitNotes.js.
 */
import { api } from "../../../services/api";

export type VisitNoteKind = "NOTE" | "NO_NOTE_NEEDED";

export interface VisitNote {
  id: string;
  version: number;
  kind: VisitNoteKind;
  body: string;
  at: string;
  by: { first_name: string; mine: boolean };
  /** One note per resident per visit day: who wrote it first, and who edited it when that was somebody else. */
  written_by: { first_name: string; mine: boolean };
  edited_by: { first_name: string } | null;
}

export interface VisitNotePage {
  ready: true;
  visit_date: string;
  note: VisitNote | null;
  max: number;
  quick_picks: string[];
}

export interface CloseoutPreview {
  visit_date: string;
  building_name: string | null;
  closed: string | null;
  residents: Array<{ patient_id: string; name: string | null; yours: boolean; note: VisitNoteKind | null; no_note_reason: string | null }>;
  missing: Array<{ patient_id: string; name: string | null; yours: boolean }>;
  counts: { seen: number; noted: number; no_note: number; files: number };
  recipient: { channel: string | null; role: string | null; name: string | null; verified: boolean };
  will_send: boolean;
  send_on: boolean;
  closeouts: Array<{ sequence: number; at: string; by_first_name: string }>;
  confirm_words: string;
  /** What became of the day's first send to the building (2026-10-28): waiting, stopped, cancelled or sent. */
  delivery?: { state: 'WAITING' | 'STOPPED' | 'CANCELLED' | 'SENT'; send_at?: string; at?: string } | null;
}

export interface CloseoutResult {
  closeout: { sequence: number; visit_date: string; seen: number; noted: number; audiograms: number };
  batch: { completed: boolean; view: string; exceptions: string[] } | null;
  send: { attempted: boolean; outcome: string | null; reason: string | null; words: string | null; held?: boolean };
}

export interface Result<T> {
  success: boolean;
  data?: T;
  code?: string;
  message?: string;
  error?: string;
  status?: number;
  detail?: any;
}

const base = (f: string) => `/emerge/day-sheet/${encodeURIComponent(f)}`;

export const visitNotesApi = {
  get: async (f: string, p: string): Promise<Result<VisitNotePage>> =>
    (await api.get(`${base(f)}/patients/${encodeURIComponent(p)}/visit-note`)) as Result<VisitNotePage>,
  write: async (f: string, p: string, text: string): Promise<Result<{ changed: boolean; note: VisitNote }>> =>
    (await api.post(`${base(f)}/patients/${encodeURIComponent(p)}/visit-note`, { text })) as Result<{ changed: boolean; note: VisitNote }>,
  noNoteNeeded: async (f: string, p: string, reason: string): Promise<Result<{ changed: boolean; note: VisitNote }>> =>
    (await api.post(`${base(f)}/patients/${encodeURIComponent(p)}/visit-note`, { no_note_needed: true, reason })) as Result<{ changed: boolean; note: VisitNote }>,
  preview: async (f: string): Promise<Result<CloseoutPreview>> => (await api.get(`${base(f)}/closeout`)) as Result<CloseoutPreview>,
  closeOut: async (f: string): Promise<Result<CloseoutResult>> => (await api.post(`${base(f)}/closeout`, { confirm: true })) as Result<CloseoutResult>,
};

/** The held reasons the server names, in words. */
export const HELD_WORDS: Record<string, string> = {
  FACILITY_RECORDS_SEND_OFF: "Sending from eMerge is switched off.",
  RECIPIENT_UNVERIFIED: "The building's records recipient is not verified yet.",
  MISSING_RECIPIENT: "The building has no current records recipient.",
  NO_SETTING: "The building has no records setting yet.",
  HOLD: "The building is set to receive nothing for now.",
  CHANNEL_PCC_UPLOAD: "This building's records are uploaded into PCC by the office.",
  CHANNEL_OTHER: "This building's records are sent by the office.",
  CHANNEL_HOLD: "The building is set to receive nothing for now.",
  BATCH_NOT_READY: "A resident's records still need something from the office.",
  ALREADY_SENT: "Nothing new since the last send.",
  SEND_STALE: "Another close-out sent it a moment ago.",
  REPLY_TO_NOT_CONFIGURED: "Sending is not set up on this server yet; the office will send it.",
  BATCH_CHANGED: "Something changed while it was being sent; the office will send it.",
  SEND_ERROR: "Something went wrong while sending; the office will send it.",
  SEND_UNKNOWN_PENDING: "The office is checking whether the last email reached the building before anything is sent again.",
  SEND_IN_FLIGHT: "A send is still in progress; the office will check it.",
  LINK_REVOKED_BY_VA: "The office stopped the last link; it decides what is sent next.",
  LINK_LOCKED: "The building's link is locked; the office will call them.",
};
export const heldWords = (code: string | null) => (code ? HELD_WORDS[code] || "The office will finish it." : "");

/**
 * What the close-out did, in words that are true: the server's words when it sent them; otherwise "sent" only for
 * SENT, "may have gone" when an outcome is not known, "already sent" for nothing new, and "nothing was sent" only
 * when nothing went.
 */
export const closeoutWords = (s: CloseoutResult["send"]): string => {
  if (s.outcome === "SENT") return "Sent to the building's records recipient.";
  if (s.outcome === "QUEUED") return s.words || "Saved. The records go to the building in 15 minutes (a first send to these recipients waits); the office can cancel it.";
  if (s.words) return s.words;
  if (s.outcome === "UNKNOWN" || s.outcome === "UNRECORDED") return "Saved. The send may have gone; the VA is checking.";
  if (s.reason === "ALREADY_SENT") return "Already sent; nothing new since the last send.";
  if (s.reason === "SEND_UNKNOWN_PENDING" || s.reason === "SEND_IN_FLIGHT") return "Saved. An earlier send may have gone; the VA is checking.";
  return "Saved and held for the VA; nothing was sent.";
};
